import { useState, useCallback } from "react";
import { MapPin, Camera, Bell, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PermissionScreen } from "./permission-screen";
import NativeLocation from "@/plugins/native-location";
import NativeCamera from "@/plugins/native-camera";
import NativeNotification from "@/plugins/native-notification";

export type PermissionType = "location" | "camera" | "notifications";
type FlowState = PermissionType | "location-soft-block" | "complete";

interface OnboardingFlowProps {
  pendingPermissions: PermissionType[];
  onComplete: () => void;
}

interface LocationSoftBlockProps {
  onRetry: () => void;
  onContinue: () => void;
}

function LocationSoftBlock({ onRetry, onContinue }: LocationSoftBlockProps) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-500" />
        </div>

        <h1 className="text-xl font-bold mb-4">Are You Sure?</h1>

        <p className="text-muted-foreground mb-8">
          Without location access, you'll need to manually enter miles for each block.
          Automatic mileage tracking won't work.
        </p>

        <div className="space-y-3">
          <Button onClick={onRetry} className="w-full" size="lg">
            Try Again
          </Button>
          <Button variant="ghost" onClick={onContinue} className="w-full">
            Continue Anyway
          </Button>
        </div>
      </div>
    </div>
  );
}

const PERMISSION_CONFIG = {
  location: {
    icon: MapPin,
    title: "Track Your Miles Automatically",
    bullets: [
      "Logs mileage while you deliver",
      "Calculates tax deductions",
      "All data stays on your device",
    ],
  },
  camera: {
    icon: Camera,
    title: "Capture Block Issues",
    bullets: [
      "Photo document delivery problems",
      "Attach evidence to blocks",
      "Quick access when you need it",
    ],
  },
  notifications: {
    icon: Bell,
    title: "Stay in the Loop",
    bullets: [
      "Shift reminders and alerts",
      "Weekly earnings summaries",
      "Never miss important updates",
    ],
  },
} as const;

export function OnboardingFlow({ pendingPermissions, onComplete }: OnboardingFlowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flowState, setFlowState] = useState<FlowState>(pendingPermissions[0]);
  const [isRequesting, setIsRequesting] = useState(false);

  const moveToNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= pendingPermissions.length) {
      onComplete();
    } else {
      setCurrentIndex(nextIndex);
      setFlowState(pendingPermissions[nextIndex]);
    }
  }, [currentIndex, pendingPermissions, onComplete]);

  const handleEnable = useCallback(async () => {
    setIsRequesting(true);

    try {
      if (flowState === "location") {
        const result = await NativeLocation.requestPermission();
        if (result.statusName === "authorizedAlways" || result.statusName === "authorizedWhenInUse") {
          moveToNext();
        } else {
          setFlowState("location-soft-block");
        }
      } else if (flowState === "camera") {
        await NativeCamera.requestPermission();
        moveToNext();
      } else if (flowState === "notifications") {
        await NativeNotification.requestPermission();
        moveToNext();
      }
    } catch (error) {
      console.error("Permission request failed:", error);
      moveToNext();
    } finally {
      setIsRequesting(false);
    }
  }, [flowState, moveToNext]);

  const handleSkip = useCallback(() => {
    if (flowState === "location") {
      setFlowState("location-soft-block");
    } else {
      moveToNext();
    }
  }, [flowState, moveToNext]);

  const handleLocationRetry = useCallback(async () => {
    setIsRequesting(true);
    try {
      const result = await NativeLocation.requestPermission();
      if (result.statusName === "authorizedAlways" || result.statusName === "authorizedWhenInUse") {
        moveToNext();
      } else {
        // Still denied, continue anyway
        moveToNext();
      }
    } catch (error) {
      moveToNext();
    } finally {
      setIsRequesting(false);
    }
  }, [moveToNext]);

  // Location soft block screen
  if (flowState === "location-soft-block") {
    return (
      <LocationSoftBlock
        onRetry={handleLocationRetry}
        onContinue={moveToNext}
      />
    );
  }

  // Regular permission screen
  const config = PERMISSION_CONFIG[flowState as PermissionType];
  if (!config) return null;

  return (
    <PermissionScreen
      icon={config.icon}
      title={config.title}
      bullets={config.bullets}
      onEnable={handleEnable}
      onSkip={handleSkip}
      isRequesting={isRequesting}
    />
  );
}
